import React, { useContext, useEffect, useMemo, useRef, useState } from "react"
import { Div, Text } from "atomize"
import { motion } from "framer-motion"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import {
	faChevronLeft,
	faChevronRight,
	faMagnifyingGlass,
	faXmark,
} from "@fortawesome/free-solid-svg-icons"
import Container from "../atoms/Container"
import TextInput from "../inputs/TextInput"
import PrimaryButton from "../buttons/PrimaryButton"
import SecondaryButton from "../buttons/SecondaryButton"
import { modalContext } from "../providers/modal.provider"
import { theme } from "../settings/config"
import { GUIDE_TOPICS, guideTopic } from "../help/guide"
import { searchGuide, searchTerms } from "../help/guide.utils"
import GuideText from "../help/GuideText"

// ──────────────────────────────────────────────────────────────────────────
// LA GUIDA
//
// Un foglio solo, con due facce: l'elenco degli argomenti, con la ricerca in
// cima, e un argomento aperto. Si passa dall'una all'altra dentro lo stesso
// foglio, perché l'app ne tiene aperto uno solo: "Guida" in alto torna
// all'elenco con la ricerca com'era.
//
// La ricerca scorre i testi stessi della guida (src/help/guide.utils.js):
// un argomento trovato mostra i pezzi di frase dove compaiono le parole.
// ──────────────────────────────────────────────────────────────────────────

// Un blocco bianco sul fondo caldo del foglio, che si tocca
const TopicBlock = ({ onClick, label, children }) => (
	<motion.div whileTap={{ scale: 0.99 }}>
		<Div
			tag="button"
			aria-label={label}
			w="100%"
			bg={theme.colors.surface}
			rounded="15px"
			p="0.75rem 1rem"
			d="flex"
			align="center"
			cursor="pointer"
			textAlign="left"
			textSize="body"
			textColor={theme.colors.gunmetal}
			style={{ gap: "0.75rem", border: 0, fontFamily: "inherit" }}
			onClick={onClick}>
			<Div flexGrow="1">{children}</Div>
			<FontAwesomeIcon icon={faChevronRight} color={theme.colors.text_light} size="sm" />
		</Div>
	</motion.div>
)

// Un pezzo di frase trovato, con le parole cercate evidenziate
const Snippet = ({ parts }) => (
	<Text
		textSize="caption"
		textColor={theme.colors.text_light}
		m={{ t: "0.3rem" }}
		style={{ lineHeight: 1.5 }}>
		{parts.map((part, i) =>
			part.hit ? (
				// un <mark> semplice: quello di atomize si portava dietro uno
				// spazio, e la parola si staccava dal resto ("recup eri")
				<mark
					key={i}
					style={{
						color: theme.colors.gunmetal,
						background: theme.colors.primary_light,
						borderRadius: "3px",
					}}>
					{part.text}
				</mark>
			) : (
				<React.Fragment key={i}>{part.text}</React.Fragment>
			),
		)}
	</Text>
)

function GuideModal({ closeModal, topicID = null }) {
	const Modal = useContext(modalContext)
	const [openID, setOpenID] = useState(guideTopic(topicID) ? topicID : null)
	const [query, setQuery] = useState("")
	const scrollRef = useRef(null)

	const topic = guideTopic(openID)
	const isSearching = searchTerms(query).length > 0
	const results = useMemo(() => searchGuide(query, GUIDE_TOPICS), [query])

	// ogni faccia, e ogni argomento, si legge dall'inizio
	useEffect(() => {
		if (scrollRef.current) scrollRef.current.scrollTop = 0
	}, [openID])

	const index = GUIDE_TOPICS.findIndex(item => item.id === openID)
	const next = index >= 0 ? GUIDE_TOPICS[index + 1] : null

	const header = topic ? (
		<>
			<Div
				tag="button"
				d="flex"
				align="center"
				bg="transparent"
				p="0"
				m={{ b: "0.5rem" }}
				cursor="pointer"
				textSize="body"
				textColor={theme.colors.text_light}
				style={{ gap: "0.4rem", border: 0, fontFamily: "inherit" }}
				onClick={() => setOpenID(null)}>
				<FontAwesomeIcon icon={faChevronLeft} size="sm" />
				<Text textColor={theme.colors.text_light}>Guida</Text>
			</Div>
			<Text tag="h2" textWeight="bold" textSize="title" m={{ b: "0.75rem" }}>
				{topic.title}
			</Text>
		</>
	) : (
		<>
			<Text tag="h2" textWeight="bold" textSize="title" m={{ b: "0.75rem" }}>
				Guida
			</Text>
			<TextInput
				value={query}
				placeholder="Cerca: assenza, recupero, codice…"
				aria-label="Cerca nella guida"
				handleChange={e => setQuery(e.target.value)}
				p={{ l: "2.75rem", r: "2.75rem" }}
				m={{ b: "0.75rem" }}
				prefix={
					<Div
						pos="absolute"
						top="50%"
						left="1.1rem"
						style={{ transform: "translateY(-50%)" }}>
						<FontAwesomeIcon icon={faMagnifyingGlass} color={theme.colors.text_light} />
					</Div>
				}
				suffix={
					query && (
						<Div
							tag="button"
							aria-label="Cancella la ricerca"
							pos="absolute"
							top="50%"
							right="0.9rem"
							w="1.75rem"
							h="1.75rem"
							rounded="circle"
							bg={theme.colors.background}
							d="flex"
							align="center"
							justify="center"
							cursor="pointer"
							style={{ transform: "translateY(-50%)", border: 0 }}
							onClick={() => setQuery("")}>
							<FontAwesomeIcon icon={faXmark} color={theme.colors.text_light} size="sm" />
						</Div>
					)
				}
			/>
		</>
	)

	return (
		<>
			<Container>{header}</Container>
			<Div ref={scrollRef} maxH="62vh" style={{ overflowY: "auto", overflowX: "hidden" }}>
				<Container>
					{topic && <GuideText blocks={topic.blocks} />}

					{!topic && isSearching && (
						<>
							<Text textSize="caption" textColor={theme.colors.text_light} m={{ b: "0.5rem" }}>
								{results.length === 0
									? "Niente, con queste parole: prova con un'altra."
									: results.length === 1
										? "In 1 argomento"
										: `In ${results.length} argomenti`}
							</Text>
							<Div d="flex" flexDir="column" style={{ gap: "0.5rem" }}>
								{results.map(result => (
									<TopicBlock
										key={result.topic.id}
										label={result.topic.title}
										onClick={() => setOpenID(result.topic.id)}>
										<Text textWeight="600">{result.topic.title}</Text>
										{result.snippets.map((parts, i) => (
											<Snippet key={i} parts={parts} />
										))}
									</TopicBlock>
								))}
							</Div>
						</>
					)}

					{!topic && !isSearching && (
						<Div d="flex" flexDir="column" style={{ gap: "0.5rem" }}>
							{GUIDE_TOPICS.map(item => (
								<TopicBlock
									key={item.id}
									label={item.title}
									onClick={() => setOpenID(item.id)}>
									<Text textWeight="500">{item.title}</Text>
									<Text textSize="caption" textColor={theme.colors.text_light}>
										{item.hint}
									</Text>
								</TopicBlock>
							))}
						</Div>
					)}

					{!topic && (
						<Div d="flex" flexDir="column" align="center" m={{ t: "1.25rem", b: "0.5rem" }}>
							<Text textSize="caption" textColor={theme.colors.text_light}>
								Non trovi quello che cerchi?
							</Text>
							<Text
								tag="button"
								bg="transparent"
								p="0.4rem"
								textSize="body"
								textColor={theme.colors.primary}
								textWeight="500"
								cursor="pointer"
								style={{ border: 0, fontFamily: "inherit" }}
								onClick={() => Modal.open("feedback")}>
								Segnala un problema
							</Text>
						</Div>
					)}
				</Container>
			</Div>

			{topic && (
				<Div
					d="flex"
					align="center"
					justify="space-between"
					m={{ t: "1rem", x: "1rem" }}
					style={{ gap: "1rem" }}>
					<SecondaryButton
						m="0"
						textColor={theme.colors.gunmetal}
						text="Chiudi"
						textSize="subheader"
						action={closeModal}
					/>
					{next && (
						<PrimaryButton
							m="0"
							bg="gunmetal_fill"
							textColor="white"
							textSize="subheader"
							text={`${next.title} ›`}
							action={() => setOpenID(next.id)}
						/>
					)}
				</Div>
			)}
		</>
	)
}

export default GuideModal
